import axiosInstance from "../utils/api";

const AUTOMATIONS_BASE = "/api/v1/automations";

const multipartHeaders = {
  "Content-Type": "multipart/form-data",
};

const ACTIVE_AUTOMATION_STATUSES = ["pending", "running"];

export const isActiveAutomationStatus = (status) =>
  ACTIVE_AUTOMATION_STATUSES.includes(String(status ?? "").toLowerCase());

const normalizeListResponse = (response) => {
  const body = response.data ?? {};
  const dataBlock =
    body.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body;

  const automations =
    dataBlock.automations ??
    body.automations ??
    (Array.isArray(body.data) ? body.data : null) ??
    (Array.isArray(body) ? body : []);

  const pagination = dataBlock.pagination ?? body.pagination ?? null;
  const normalizedPagination = pagination
    ? {
        current_page: pagination.current_page ?? pagination.page ?? 1,
        per_page: pagination.per_page ?? pagination.size ?? 20,
        total_pages: pagination.total_pages ?? 1,
        total_count: pagination.total_count ?? automations.length,
      }
    : null;

  const listColumns = dataBlock.list_columns ?? body.list_columns ?? null;

  return {
    automations: Array.isArray(automations) ? automations : [],
    pagination: normalizedPagination,
    listColumns: Array.isArray(listColumns) && listColumns.length > 0 ? listColumns : null,
  };
};

export const buildAutomationListParams = (params = {}) => {
  const { page, per_page, search, column, diy_agent_id, ...rest } = params;
  const query = { ...rest };

  if (page != null) query.page = page;
  if (per_page != null) query.per_page = per_page;
  if (diy_agent_id != null) query.diy_agent_id = diy_agent_id;

  const trimmedSearch = typeof search === "string" ? search.trim() : "";
  if (trimmedSearch) query.search = trimmedSearch;
  if (column) query.column = column;

  return query;
};

export const fetchAutomations = async (params = {}, config = {}) => {
  const response = await axiosInstance.get(AUTOMATIONS_BASE, {
    params: buildAutomationListParams(params),
    ...config,
  });
  return normalizeListResponse(response);
};

export const fetchAutomationByAgentId = async (agentId) => {
  if (!agentId) return null;

  try {
    const response = await axiosInstance.get(AUTOMATIONS_BASE, {
      params: { diy_agent_id: agentId },
    });
    const { automations } = normalizeListResponse(response);
    return automations[0] ?? null;
  } catch (error) {
    if (error.response?.status === 404) return null;
    throw error;
  }
};

export const fetchAutomationById = async (automationId) => {
  if (!automationId) return null;
  const response = await axiosInstance.get(`${AUTOMATIONS_BASE}/${automationId}`);
  return response.data?.data ?? response.data?.automation ?? response.data ?? null;
};

export const buildAutomationFormData = (form, automationId = null) => {
  const formData = new FormData();

  if (automationId) {
    formData.append("automation[id]", String(automationId));
  }

  formData.append("automation[workflow_name]", form.workflowName?.trim() ?? "");

  if (form.workflowKey) {
    formData.append("automation[workflow_key]", form.workflowKey);
  }
  if (form.agentDuration !== "" && form.agentDuration != null) {
    formData.append("automation[agent_duration]", String(form.agentDuration));
  }
  formData.append("automation[scheduler_time]", String(form.schedulerTime ?? ""));

  const agentIdPairs = form.agentIdsKv ?? [];
  agentIdPairs.forEach(({ key, value }) => {
    const trimmedKey = key?.trim();
    if (!trimmedKey) return;
    formData.append(`automation[agent_ids][${trimmedKey}]`, String(value ?? ""));
  });

  const instructionPairs = form.instructionsKv ?? [];
  instructionPairs.forEach(({ key, value }) => {
    const trimmedKey = key?.trim();
    if (!trimmedKey) return;
    formData.append(`automation[instructions][${trimmedKey}]`, String(value ?? ""));
  });

  const contextPairs = form.contextKv ?? [];
  contextPairs.forEach(({ key, value }) => {
    const trimmedKey = key?.trim();
    if (!trimmedKey) return;
    formData.append(`automation[context][${trimmedKey}]`, String(value ?? ""));
  });

  const dataFiles = (form.dataFiles ?? []).filter((entry) => {
    if (entry._destroy) return Boolean(entry.name?.trim());
    return Boolean(entry.file);
  });

  dataFiles.forEach((entry, index) => {
    const baseKey = `automation[automation_data_files_attributes][${index}]`;

    if (entry._destroy) {
      if (entry.recordId) {
        formData.append(`${baseKey}[id]`, String(entry.recordId));
      }
      formData.append(`${baseKey}[_destroy]`, "true");
      if (entry.name?.trim()) {
        formData.append(`${baseKey}[name]`, entry.name.trim());
      }
      return;
    }

    if (entry.name) {
      formData.append(`${baseKey}[name]`, entry.name);
    }
    if (entry.file) {
      formData.append(`${baseKey}[file]`, entry.file);
    }
  });

  return formData;
};

export const createAutomation = async (form) => {
  const formData = buildAutomationFormData(form);
  const response = await axiosInstance.post(AUTOMATIONS_BASE, formData, {
    headers: multipartHeaders,
  });
  return response.data?.data ?? response.data?.automation ?? response.data;
};

export const updateAutomation = async (automationId, form) => {
  const formData = buildAutomationFormData(form, automationId);
  const response = await axiosInstance.put(`${AUTOMATIONS_BASE}/${automationId}`, formData, {
    headers: multipartHeaders,
  });
  return response.data?.data ?? response.data?.automation ?? response.data;
};

export const deleteAutomation = async (automationId) => {
  const response = await axiosInstance.delete(`${AUTOMATIONS_BASE}/${automationId}`);
  return response.data;
};

export const runAutomation = async (automationId) => {
  const response = await axiosInstance.post(`${AUTOMATIONS_BASE}/${automationId}/run`);
  return response.data?.data ?? response.data;
};
